// Simple Authentication Service - Works immediately without external dependencies
class SimpleAuthService {
  constructor() {
    this.baseURL = 'https://social-media-api.andrewrkloc.workers.dev/api';
    this.demoUsers = [
      {
        id: 'user-1',
        email: 'demo@example.com',
        password: 'demo123',
        name: 'Andrew Kloc',
        company: 'Andrew Kloc Digital',
        subscription_tier: 'premium',
        subscription_status: 'active',
        payment_verified: true,
        trial_ends_at: null,
        socialAccounts: {
          facebook: true,
          instagram: true,
          twitter: true,
          linkedin: false,
          youtube: false
        }
      },
      {
        id: 'admin-1',
        email: 'admin@example.com',
        password: 'admin123',
        name: 'Andrew Kloc',
        company: 'Andrew Kloc Digital',
        role: 'admin',
        subscription_tier: 'admin',
        subscription_status: 'active',
        payment_verified: true
      }
    ];
  }

  // CRITICAL FIX: Clear all cached data
  clearAllCachedData() {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientData');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    localStorage.removeItem('companyName');
    localStorage.removeItem('userCompany');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userProfile');
    console.log('🧹 DEBUG: Cleared all cached data');
  }

  // Force logout and clear all data
  forceLogout() {
    this.clearAllCachedData();
    window.location.href = '/';
  }

  // Reset to default profile
  resetToDefaultProfile() {
    this.clearAllCachedData();
    const defaultProfile = {
      name: 'Andrew Kloc',
      company: 'Andrew Kloc Digital',
      email: 'demo@example.com'
    };
    
    localStorage.setItem('companyName', defaultProfile.company);
    localStorage.setItem('userCompany', defaultProfile.company);
    
    console.log('🔧 DEBUG: Reset to default profile:', defaultProfile);
    return defaultProfile;
  }

  // Sign in user
  async signIn(email, password) {
    try {
      // CRITICAL FIX: Clear any existing cached data first
      this.clearAllCachedData();
      
      // Check demo users first
      const demoUser = this.demoUsers.find(user => 
        user.email === email && user.password === password
      );

      if (demoUser) {
        const token = `demo-token-${demoUser.id}-${Date.now()}`;
        
        // CRITICAL FIX: Save company name to localStorage immediately
        if (demoUser.company) {
          localStorage.setItem('companyName', demoUser.company);
          localStorage.setItem('userCompany', demoUser.company);
          console.log('🔧 DEBUG: Saved demo company name to localStorage:', demoUser.company);
        }
        
        return {
          success: true,
          user: demoUser,
          token: token,
          error: null
        };
      }

      // Try backend API
      const response = await fetch(`${this.baseURL}/auth/client-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (data.success) {
        // CRITICAL FIX: Save company name to localStorage immediately
        if (data.client.company) {
          localStorage.setItem('companyName', data.client.company);
          localStorage.setItem('userCompany', data.client.company);
          console.log('🔧 DEBUG: Saved API company name to localStorage:', data.client.company);
        }
        
        return {
          success: true,
          user: data.client,
          token: data.token,
          error: null
        };
      } else {
        return {
          success: false,
          user: null,
          token: null,
          error: data.error || 'Invalid credentials'
        };
      }
    } catch (error) {
      console.error('Sign in error:', error);
      return {
        success: false,
        user: null,
        token: null,
        error: 'Network error. Please try again.'
      };
    }
  }

  // Sign up user
  async signUp(email, password, userData) {
    try {
      // CRITICAL FIX: Clear any existing cached data first
      this.clearAllCachedData();
      
      // Check if user already exists
      const existingUser = this.demoUsers.find(user => user.email === email);
      if (existingUser) {
        return {
          success: false,
          user: null,
          token: null,
          error: 'User with this email already exists'
        };
      }

      // Try backend API
      const response = await fetch(`${this.baseURL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          name: userData.fullName,
          company: userData.company
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // CRITICAL FIX: Save company name to localStorage immediately
        if (userData.company) {
          localStorage.setItem('companyName', userData.company);
          localStorage.setItem('userCompany', userData.company);
          console.log('🔧 DEBUG: Saved new company name to localStorage:', userData.company);
        }
        
        return {
          success: true,
          user: data.user,
          token: data.token,
          error: null
        };
      } else {
        return {
          success: false,
          user: null,
          token: null,
          error: data.error || 'Registration failed'
        };
      }
    } catch (error) {
      console.error('Sign up error:', error);
      return {
        success: false,
        user: null,
        token: null,
        error: 'Network error. Please try again.'
      };
    }
  }

  // Sign out user
  async signOut() {
    try {
      // CRITICAL FIX: Clear ALL cached data
      this.clearAllCachedData();
      
      return { success: true, error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error: 'Sign out failed' };
    }
  }

  // Get current user from localStorage
  getCurrentUser() {
    try {
      const clientData = localStorage.getItem('clientData');
      const adminData = localStorage.getItem('adminData');
      
      if (clientData) {
        return JSON.parse(clientData);
      } else if (adminData) {
        return JSON.parse(adminData);
      }
      
      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('clientToken') || localStorage.getItem('adminToken');
    return !!token;
  }

  // CRITICAL FIX: Get current company name - PRIORITY ORDER
  getCurrentCompanyName() {
    // 1. First priority: userCompany (most reliable)
    const userCompany = localStorage.getItem('userCompany');
    if (userCompany) {
      console.log('🔧 DEBUG: Retrieved company name from userCompany:', userCompany);
      return userCompany;
    }
    
    // 2. Second priority: companyName
    const companyName = localStorage.getItem('companyName');
    if (companyName) {
      console.log('🔧 DEBUG: Retrieved company name from companyName:', companyName);
      return companyName;
    }
    
    // 3. Third priority: current user data
    const currentUser = this.getCurrentUser();
    if (currentUser?.company) {
      console.log('🔧 DEBUG: Retrieved company name from currentUser.company:', currentUser.company);
      return currentUser.company;
    }
    
    // 4. Fourth priority: user metadata
    if (currentUser?.user_metadata?.company_name) {
      console.log('🔧 DEBUG: Retrieved company name from user_metadata.company_name:', currentUser.user_metadata.company_name);
      return currentUser.user_metadata.company_name;
    }
    
    if (currentUser?.user_metadata?.company) {
      console.log('🔧 DEBUG: Retrieved company name from user_metadata.company:', currentUser.user_metadata.company);
      return currentUser.user_metadata.company;
    }
    
    // 5. Last resort: default
    console.log('🔧 DEBUG: No company name found, using default');
    return 'Andrew Kloc Digital';
  }

  // CRITICAL FIX: Update company name
  updateCompanyName(companyName) {
    localStorage.setItem('companyName', companyName);
    localStorage.setItem('userCompany', companyName);
    console.log('🔧 DEBUG: Updated company name in localStorage:', companyName);
  }

  // CRITICAL FIX: Force refresh company name
  forceRefreshCompanyName() {
    const companyName = this.getCurrentCompanyName();
    console.log('🔧 DEBUG: Force refreshed company name:', companyName);
    return companyName;
  }

  // Validate token (simple check)
  async validateToken(token) {
    try {
      // For demo purposes, accept any token that exists
      if (token && token.startsWith('demo-token-')) {
        return { valid: true, user: this.getCurrentUser() };
      }

      // Try backend validation
      const response = await fetch(`${this.baseURL}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      return { valid: data.valid, user: data.user };
    } catch (error) {
      console.error('Token validation error:', error);
      return { valid: false, user: null };
    }
  }
}

export const simpleAuth = new SimpleAuthService();
export default simpleAuth;
